import React, { useState } from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';
import moment from 'moment';

import { TYPES, STATUS } from '../../../../../server/constants';
import { Container, Button, Segment } from 'semantic-ui-react';
import { getValue, notNilOrEmpty, isNilOrEmpty } from '_utils';
import { sixMonthsInMinutes } from '_constants';

import NewLicense from './newLicense';
import License from '_views/license';
import { isAuthAdmin, isLicenseAdmin } from '../../../../utils';
import useAdminUser from '../../../../hooks/useAdminUser';

const UserLicenses = ({
  licenses,
  setNavigation,
  onAddLicense,
  onModifyLicense,
  onReactivateLicense,
  onRemoveLicense,
  onAddSublicense,
  onRemoveSublicense,
  onRemoveLicenseMachine,
  onDeactivateLicenseUpgrade,
}) => {
  const adminUser = useAdminUser();

  const newLicenseDefaults = {
    machine_count: 4,
    session_length_min: sixMonthsInMinutes,
    session_renewable: false,
    ilok_accounts: [],
    expires_on: moment(new Date()).add(30, 'days').format('YYYY-MM-DD'),
    name: 'max9',
    type: 'full',
  };
  const [addingLicense, setAddingLicense] = useState(false);
  const [newLicense, setNewLicense] = useState(newLicenseDefaults);

  const cleanIloks = R.compose(
    R.ifElse(
      isNilOrEmpty,
      R.always(null),
      R.identity
    ),
    R.filter(notNilOrEmpty),
    R.map(R.trim),
    R.propOr([], 'ilok_accounts')
  );

  const maxTypeToNumber = R.cond([
    [R.propEq('type', TYPES.FULL), R.always(4)],
    [R.propEq('type', TYPES.SUBSCRIPTION), R.always(3)],
    [R.propEq('type', TYPES.TERM), R.always(2)],
    [R.T, R.always(1)]
  ]);

  const sortLics = R.sortWith([
    R.descend(R.propEq('status', STATUS.ACTIVE)), // sort by status
    R.descend(R.prop('name')), // sort by version, lowest to highest, if we request with max version 7, we take any max7 first
    R.descend(maxTypeToNumber), // sort by type, take full over sub over term over anything else
    R.descend(R.prop('expires_on')) // sort by expiration date, the longest expiration first
  ]);

  const handleNewLicenseValueChange = (e, { name, value, type, checked }) => {
    const newLic = R.merge(newLicense, { [name]: getValue({ name, value, type, checked }) });
    setNewLicense(newLic);
  };

  const handleNewLicenseCancel = () => {
    setAddingLicense(false);
  };

  const handleNewLicenseSave = () => {
    const license = R.pickBy(notNilOrEmpty,
      R.merge(newLicense, {
        ilok_accounts: cleanIloks(newLicense),
        expires_on: newLicense.type === 'full' ? null : newLicense.expires_on,
      })
    );

    onAddLicense(license);
    setAddingLicense(false);
  };

  return (
    <Container fluid>
      {
        isAuthAdmin(adminUser) || isLicenseAdmin(adminUser) ? (
          <Button onClick={() => setAddingLicense(true)} disabled={addingLicense}>
            Add License
          </Button>
        ) : null
      }

      <Segment.Group>
        {addingLicense ? (
          <NewLicense
            onChange={handleNewLicenseValueChange}
            onCancel={handleNewLicenseCancel}
            onSave={handleNewLicenseSave}
            license={newLicense}
          />
        ) : null}
        {sortLics(licenses).map((license) => (
          <License
            key={license._id}
            license={license}
            setNavigation={setNavigation}
            onModifyLicense={onModifyLicense}
            onReactivateLicense={onReactivateLicense}
            onRemoveLicense={onRemoveLicense}
            onAddSublicense={onAddSublicense}
            onRemoveSublicense={onRemoveSublicense}
            onRemoveLicenseMachine={onRemoveLicenseMachine}
            onDeactivateLicenseUpgrade={onDeactivateLicenseUpgrade}
          />
        ))}
      </Segment.Group>
    </Container>
  );
};

UserLicenses.propTypes = {
  licenses: PropTypes.array.isRequired,
  setNavigation: PropTypes.func.isRequired,
  onAddLicense: PropTypes.func.isRequired,
  onReactivateLicense: PropTypes.func.isRequired,
  onModifyLicense: PropTypes.func.isRequired,
  onRemoveLicense: PropTypes.func.isRequired,
  onAddSublicense: PropTypes.func.isRequired,
  onRemoveSublicense: PropTypes.func.isRequired,
  onRemoveLicenseMachine: PropTypes.func.isRequired,
  onDeactivateLicenseUpgrade: PropTypes.func.isRequired,
};

export default UserLicenses;



// WEBPACK FOOTER //
// ./webAdmin/components/users/user/licenses/index.js